import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FONTS, COLORS } from '../utils/styleConstants';
import { useAuth } from '../context/AuthContext';
import headerLogo from '../assets/header_logo.svg';
import sidebarLogo from '../assets/sidebar_logo.svg';
import ofertaSimbolo from '../assets/aaff OV_logo_rgb_1000px SIMBOLO 02.svg';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
  overflow-x: hidden;
`;

const Navigation = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  padding: 1rem 0;
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #01174f;
  text-decoration: none;
  font-weight: 500;
  font-family: ${FONTS.PRIMARY};
  transition: color 0.3s;
  cursor: pointer;

  &:hover {
    color: #ff7057;
  }
`;

const CTAButton = styled(Link)`
  background: #ff7057;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(255, 112, 87, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 112, 87, 0.4);
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, #01174f 0%, #0a2463 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -30%;
    width: 100%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 112, 87, 0.1) 0%, transparent 70%);
    animation: ${float} 20s ease-in-out infinite;
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  animation: ${fadeIn} 1s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  color: white;
  margin-bottom: 1.5rem;
  font-family: ${FONTS.PRIMARY};
  font-weight: 700;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: #a7c3eb;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  font-family: ${FONTS.SECONDARY};
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(Link)`
  background: #ff7057;
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  font-size: 1.1rem;
  transition: all 0.3s;
  box-shadow: 0 4px 20px rgba(255, 112, 87, 0.4);
  display: inline-block;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 30px rgba(255, 112, 87, 0.5);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  font-size: 1.1rem;
  border: 2px solid white;
  transition: all 0.3s;
  display: inline-block;
  cursor: pointer;

  &:hover {
    background: white;
    color: #01174f;
  }
`;

const SecondaryButtonStyled = styled.button`
  background: transparent;
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  font-size: 1.1rem;
  border: 2px solid white;
  transition: all 0.3s;
  display: inline-block;
  cursor: pointer;

  &:hover {
    background: white;
    color: #01174f;
  }
`;

const HeroImage = styled.div`
  position: relative;
  animation: ${fadeIn} 1s ease-out 0.3s both;

  @media (max-width: 968px) {
    display: none;
  }
`;

const FloatingCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 2rem;
  position: absolute;
  ${props => props.top && `top: ${props.top};`}
  ${props => props.left && `left: ${props.left};`}
  ${props => props.right && `right: ${props.right};`}
  ${props => props.bottom && `bottom: ${props.bottom};`}
  animation: ${float} ${props => props.duration || '3s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: #ff7057;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  color: white;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-family: ${FONTS.PRIMARY};
`;

const CardText = styled.p`
  color: #a7c3eb;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
`;

const FeaturesSection = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeIn} 1s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: #01174f;
  margin-bottom: 1rem;
  font-family: ${FONTS.PRIMARY};
  font-weight: 700;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #556fb5;
  max-width: 600px;
  margin: 0 auto;
  font-family: ${FONTS.SECONDARY};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  background: #f8f9fa;
  border-radius: 20px;
  padding: 2.5rem;
  transition: all 0.3s;
  border: 1px solid transparent;
  animation: ${fadeIn} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border-color: #ff7057;
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ff7057 0%, #ff8a73 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 2rem;
  color: white;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  color: #01174f;
  margin-bottom: 1rem;
  font-family: ${FONTS.PRIMARY};
`;

const FeatureDescription = styled.p`
  color: #556fb5;
  line-height: 1.6;
  font-family: ${FONTS.SECONDARY};
`;

const ProcessSection = styled.section`
  padding: 5rem 2rem;
  background: #f8f9fa;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ProcessStep = styled.div`
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: #01174f;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
  font-family: ${FONTS.PRIMARY};
`;

const StepTitle = styled.h4`
  font-size: 1.2rem;
  color: #01174f;
  margin-bottom: 0.5rem;
  font-family: ${FONTS.PRIMARY};
`;

const StepDescription = styled.p`
  color: #556fb5;
  font-family: ${FONTS.SECONDARY};
`;

const TestimonialsSection = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const TestimonialCard = styled.div`
  background: #f8f9fa;
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  animation: ${fadeIn} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};

  &::before {
    content: '"';
    position: absolute;
    top: -10px;
    left: 20px;
    font-size: 4rem;
    color: #ff7057;
    opacity: 0.3;
    font-family: serif;
  }
`;

const TestimonialText = styled.p`
  color: #556fb5;
  font-style: italic;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-family: ${FONTS.SECONDARY};
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #01174f 0%, #0a2463 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-family: ${FONTS.PRIMARY};
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.h5`
  color: #01174f;
  margin-bottom: 0.2rem;
  font-family: ${FONTS.PRIMARY};
`;

const AuthorTitle = styled.p`
  color: #556fb5;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
`;

const CTASection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #01174f 0%, #0a2463 100%);
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  font-family: ${FONTS.PRIMARY};
  animation: ${fadeIn} 1s ease-out;
`;

const CTASubtitle = styled.p`
  font-size: 1.2rem;
  color: #a7c3eb;
  margin-bottom: 2.5rem;
  font-family: ${FONTS.SECONDARY};
  animation: ${fadeIn} 1s ease-out 0.2s both;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: ${fadeIn} 1s ease-out 0.4s both;
`;

const Footer = styled.footer`
  background: #01174f;
  color: white;
  padding: 3rem 2rem 2rem;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FooterColumn = styled.div``;

const FooterTitle = styled.h4`
  margin-bottom: 1rem;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
`;

const FooterLink = styled(Link)`
  display: block;
  color: #a7c3eb;
  text-decoration: none;
  margin-bottom: 0.5rem;
  font-family: ${FONTS.SECONDARY};
  transition: color 0.3s;

  &:hover {
    color: white;
  }
`;

const FooterBottom = styled.div`
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #a7c3eb;
  font-family: ${FONTS.SECONDARY};
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #01174f;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 1rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    z-index: 999;
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

const MobileNavLink = styled(NavLink)`
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  display: block;
  text-align: center;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileCTAButton = styled(CTAButton)`
  margin: 1rem;
  text-align: center;
`;

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Función para hacer scroll suave a las secciones
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false); // Cerrar menú móvil al hacer click
    }
  };

  return (
    <PageContainer>
      <Navigation>
        <NavContainer>
          <LogoContainer>
            <Logo src={headerLogo} alt="Ofertas Vinculantes" />
          </LogoContainer>
          <NavLinks>
            <NavLink onClick={() => scrollToSection('features')}>Características</NavLink>
            <NavLink onClick={() => scrollToSection('process')}>Cómo funciona</NavLink>
            <NavLink onClick={() => scrollToSection('testimonials')}>Testimonios</NavLink>
            {isAuthenticated ? (
              <>
                <NavLink as={Link} to="/offers">Mis expedientes</NavLink>
                <CTAButton to="/nueva-oferta">Nueva oferta</CTAButton>
              </>
            ) : (
              <>
                <NavLink as={Link} to="/login">Iniciar sesión</NavLink>
                <CTAButton to="/register">Comenzar ahora</CTAButton>
              </>
            )}
          </NavLinks>
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        </NavContainer>
      </Navigation>

      <MobileMenu isOpen={mobileMenuOpen}>
        <MobileNavLink onClick={() => scrollToSection('features')}>Características</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('process')}>Cómo funciona</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('testimonials')}>Testimonios</MobileNavLink>
        {isAuthenticated ? (
          <>
            <MobileNavLink as={Link} to="/offers">Mis expedientes</MobileNavLink>
            <MobileCTAButton to="/nueva-oferta">Nueva oferta</MobileCTAButton>
          </>
        ) : (
          <>
            <MobileNavLink as={Link} to="/login">Iniciar sesión</MobileNavLink>
            <MobileCTAButton to="/register">Comenzar ahora</MobileCTAButton>
          </>
        )}
      </MobileMenu>

      <HeroSection>
        <HeroContainer>
          <HeroContent>
            <HeroTitle>
              Gestión Digital de Ofertas Vinculantes para Profesionales del Derecho
            </HeroTitle>
            <HeroSubtitle>
              Automatiza y asegura tus procesos legales con nuestra plataforma especializada. 
              Envía, negocia y valida ofertas vinculantes con total seguridad jurídica.
            </HeroSubtitle>
            <HeroButtons>
              {isAuthenticated ? (
                <>
                  <PrimaryButton to="/nueva-oferta">Crear nueva oferta</PrimaryButton>
                  <SecondaryButton to="/offers">Ver mis expedientes</SecondaryButton>
                </>
              ) : (
                <>
                  <PrimaryButton to="/register">Prueba gratuita</PrimaryButton>
                  <SecondaryButtonStyled onClick={() => scrollToSection('features')}>Descubre más</SecondaryButtonStyled>
                </>
              )}
            </HeroButtons>
          </HeroContent>
          <HeroImage>
            <FloatingCard top="20%" right="10%" duration="4s">
              <IconWrapper>📋</IconWrapper>
              <CardTitle>Gestión eficiente</CardTitle>
              <CardText>Automatiza todo el proceso</CardText>
            </FloatingCard>
            <FloatingCard bottom="20%" left="10%" duration="5s" delay="1s">
              <IconWrapper>🔒</IconWrapper>
              <CardTitle>100% Seguro</CardTitle>
              <CardText>Cifrado de extremo a extremo</CardText>
            </FloatingCard>
          </HeroImage>
        </HeroContainer>
      </HeroSection>

      <FeaturesSection id="features">
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Todo lo que necesitas en un solo lugar</SectionTitle>
            <SectionSubtitle>
              Herramientas diseñadas específicamente para abogados y profesionales legales
            </SectionSubtitle>
          </SectionHeader>
          <FeaturesGrid>
            <FeatureCard delay="0s">
              <FeatureIcon>📤</FeatureIcon>
              <FeatureTitle>Envío Instantáneo</FeatureTitle>
              <FeatureDescription>
                Envía ofertas vinculantes de forma inmediata con validación automática 
                de todos los requisitos legales necesarios.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard delay="0.2s">
              <FeatureIcon>🤝</FeatureIcon>
              <FeatureTitle>Negociación Digital</FeatureTitle>
              <FeatureDescription>
                Plataforma integrada para negociar términos y condiciones de manera 
                eficiente, con trazabilidad completa del proceso.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard delay="0.4s">
              <FeatureIcon>✅</FeatureIcon>
              <FeatureTitle>Validación Automática</FeatureTitle>
              <FeatureDescription>
                Sistema inteligente que verifica el cumplimiento normativo y genera 
                certificados legales automáticamente.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard delay="0.6s">
              <FeatureIcon>🔐</FeatureIcon>
              <FeatureTitle>Seguridad Jurídica</FeatureTitle>
              <FeatureDescription>
                Cumplimiento total con la normativa vigente, cifrado de datos y 
                firma electrónica cualificada integrada.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard delay="0.8s">
              <FeatureIcon>📊</FeatureIcon>
              <FeatureTitle>Gestión de Expedientes</FeatureTitle>
              <FeatureDescription>
                Organiza y accede a todos tus expedientes desde un panel centralizado 
                con búsqueda avanzada y filtros.
              </FeatureDescription>
            </FeatureCard>
            <FeatureCard delay="1s">
              <FeatureIcon>⏰</FeatureIcon>
              <FeatureTitle>Alertas y Plazos</FeatureTitle>
              <FeatureDescription>
                Sistema de notificaciones automáticas para no perder ningún plazo 
                importante en tus ofertas vinculantes.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </SectionContainer>
      </FeaturesSection>

      <ProcessSection id="process">
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Cómo funciona</SectionTitle>
            <SectionSubtitle>
              Un proceso simple y eficiente en solo 4 pasos
            </SectionSubtitle>
          </SectionHeader>
          <ProcessSteps>
            <ProcessStep delay="0s">
              <StepNumber>1</StepNumber>
              <StepTitle>Regístrate</StepTitle>
              <StepDescription>
                Crea tu cuenta profesional en minutos con verificación de identidad
              </StepDescription>
            </ProcessStep>
            <ProcessStep delay="0.2s">
              <StepNumber>2</StepNumber>
              <StepTitle>Crea tu oferta</StepTitle>
              <StepDescription>
                Completa el formulario inteligente con los datos de la oferta vinculante
              </StepDescription>
            </ProcessStep>
            <ProcessStep delay="0.4s">
              <StepNumber>3</StepNumber>
              <StepTitle>Envía y negocia</StepTitle>
              <StepDescription>
                Envía la oferta a la contraparte y gestiona la negociación en línea
              </StepDescription>
            </ProcessStep>
            <ProcessStep delay="0.6s">
              <StepNumber>4</StepNumber>
              <StepTitle>Finaliza el proceso</StepTitle>
              <StepDescription>
                Obtén el certificado legal y archiva el expediente automáticamente
              </StepDescription>
            </ProcessStep>
          </ProcessSteps>
        </SectionContainer>
      </ProcessSection>

      <TestimonialsSection id="testimonials">
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Lo que dicen nuestros usuarios</SectionTitle>
            <SectionSubtitle>
              Miles de abogados ya confían en nuestra plataforma
            </SectionSubtitle>
          </SectionHeader>
          <TestimonialsGrid>
            <TestimonialCard delay="0s">
              <TestimonialText>
                "Esta plataforma ha revolucionado la forma en que gestionamos las ofertas 
                vinculantes en nuestro despacho. Ahorramos tiempo y ganamos en seguridad jurídica."
              </TestimonialText>
              <TestimonialAuthor>
                <AuthorAvatar>MG</AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>María González</AuthorName>
                  <AuthorTitle>Socia - González & Asociados</AuthorTitle>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
            <TestimonialCard delay="0.2s">
              <TestimonialText>
                "La automatización del proceso y la validación automática nos han permitido 
                reducir errores y mejorar significativamente nuestra eficiencia."
              </TestimonialText>
              <TestimonialAuthor>
                <AuthorAvatar>JM</AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>Juan Martínez</AuthorName>
                  <AuthorTitle>Director Legal - Corporación JM</AuthorTitle>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
            <TestimonialCard delay="0.4s">
              <TestimonialText>
                "Excelente herramienta para mantener la trazabilidad completa de todas las 
                negociaciones. La seguridad y el cumplimiento normativo son impecables."
              </TestimonialText>
              <TestimonialAuthor>
                <AuthorAvatar>AR</AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>Ana Rodríguez</AuthorName>
                  <AuthorTitle>Abogada Senior - AR Legal</AuthorTitle>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          </TestimonialsGrid>
        </SectionContainer>
      </TestimonialsSection>

      <CTASection>
        <SectionContainer>
          <CTATitle>
            {isAuthenticated 
              ? '¿Listo para crear tu próxima oferta vinculante?' 
              : '¿Listo para digitalizar tus ofertas vinculantes?'}
          </CTATitle>
          <CTASubtitle>
            {isAuthenticated
              ? 'Accede a todas las herramientas profesionales para gestionar tus expedientes'
              : 'Únete a miles de profesionales del derecho que ya han modernizado sus procesos'}
          </CTASubtitle>
          <CTAButtons>
            {isAuthenticated ? (
              <>
                <PrimaryButton to="/nueva-oferta">Crear nueva oferta</PrimaryButton>
                <SecondaryButton to="/offers">Ver expedientes</SecondaryButton>
              </>
            ) : (
              <>
                <PrimaryButton to="/register">Comenzar prueba gratuita</PrimaryButton>
                <SecondaryButton to="/login">Ya tengo cuenta</SecondaryButton>
              </>
            )}
          </CTAButtons>
        </SectionContainer>
      </CTASection>

      <Footer>
        <FooterContainer>
          <FooterColumn>
            <FooterTitle>Producto</FooterTitle>
            <FooterLink to="#features">Características</FooterLink>
            <FooterLink to="#process">Cómo funciona</FooterLink>
            <FooterLink to="#pricing">Precios</FooterLink>
          </FooterColumn>
          <FooterColumn>
            <FooterTitle>Empresa</FooterTitle>
            <FooterLink to="#about">Sobre nosotros</FooterLink>
            <FooterLink to="#contact">Contacto</FooterLink>
            <FooterLink to="#careers">Trabaja con nosotros</FooterLink>
          </FooterColumn>
          <FooterColumn>
            <FooterTitle>Legal</FooterTitle>
            <FooterLink to="/terminos">Términos y condiciones</FooterLink>
            <FooterLink to="/privacidad">Política de privacidad</FooterLink>
            <FooterLink to="#cookies">Política de cookies</FooterLink>
          </FooterColumn>
          <FooterColumn>
            <FooterTitle>Soporte</FooterTitle>
            <FooterLink to="#help">Centro de ayuda</FooterLink>
            <FooterLink to="#faq">Preguntas frecuentes</FooterLink>
            <FooterLink to="#contact">Contactar soporte</FooterLink>
          </FooterColumn>
        </FooterContainer>
        <FooterBottom>
          <p>&copy; 2024 Ofertas Vinculantes. Todos los derechos reservados.</p>
        </FooterBottom>
      </Footer>
    </PageContainer>
  );
};

export default HomePage; 